/**
 * MODULE: domain/sales-intelligence/frontend/hooks/useSellerContext.ts
 * PURPOSE: Hooks for seller context (check, quick setup, get, update) with TanStack Query + legacy imperative API.
 * DEPENDENCIES: @tanstack/react-query, ../api/client, ../services/contextService, contracts types
 * PUBLIC_API: useSellerContext(), useSaveQuickSetup(), useCheckSellerContext(), useSellerProfile()
 * SIDE_EFFECTS: API calls to /api/v1/sales-intelligence/context/*
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: 185 / 200 lines
 * Recent: 2026-02-06 Seller context (JIT) – used by QuickSetupModal and context check flow.
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sellerContextApi } from '../api/client';
import {
  checkSellerContext,
  saveQuickSetupContext,
  getSellerProfile,
  deleteSellerProfile
} from '../services/contextService';
import type {
  CheckContextResponse,
  QuickSetupFormData,
  SaveQuickSetupResponse,
  SellerCompanyProfile
} from '../../contracts/sales-intelligence.types';
import type { SellerQuickSetupFormState } from '../types/seller-context';

const CONTEXT_QUERY_KEY = ['seller-context'];
const PROFILE_QUERY_KEY = ['seller-profile'];

export interface UseSellerContextReturn {
  checkContext: () => Promise<CheckContextResponse>;
  saveQuickSetup: (data: QuickSetupFormData) => Promise<SaveQuickSetupResponse>;
  getContext: () => Promise<SellerCompanyProfile | null>;
  updateContext: (updates: Partial<SellerCompanyProfile>) => Promise<SellerCompanyProfile>;
  loading: boolean;
  error: string | null;
}

export function useSellerContext(): UseSellerContextReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkContext = useCallback(async (): Promise<CheckContextResponse> => {
    setLoading(true);
    setError(null);
    try {
      const result = await sellerContextApi.check();
      return result;
    } catch (err: unknown) {
      const errMsg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : null;
      const message = errMsg || 'Failed to check seller context';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveQuickSetup = useCallback(async (data: QuickSetupFormData): Promise<SaveQuickSetupResponse> => {
    setLoading(true);
    setError(null);
    try {
      const requestData = {
        companyName: data.companyName,
        valueProposition: data.valueProp,
        idealCustomerProfile: {
          industries: data.targetIndustries,
          companySize: data.idealSize,
          technologies: data.integrations ?? [],
          painPoints: data.painPoints ? [data.painPoints] : []
        },
        competitors: data.competitors ?? [],
        products: data.products ?? []
      };
      const result = await sellerContextApi.quickSetup(requestData);
      return result;
    } catch (err: unknown) {
      const errMsg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : null;
      const message = errMsg || 'Failed to save quick setup';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const getContext = useCallback(async (): Promise<SellerCompanyProfile | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await sellerContextApi.get();
      return result;
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) return null;
      }
      const errMsg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : null;
      const message = errMsg || 'Failed to get seller context';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateContext = useCallback(async (updates: Partial<SellerCompanyProfile>): Promise<SellerCompanyProfile> => {
    setLoading(true);
    setError(null);
    try {
      const result = await sellerContextApi.update(updates);
      return result;
    } catch (err: unknown) {
      const errMsg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : null;
      const message = errMsg || 'Failed to update seller context';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    checkContext,
    saveQuickSetup,
    getContext,
    updateContext,
    loading,
    error
  };
}

// -----------------------------------------
// TanStack Query hooks (JIT context flow)
// -----------------------------------------

/** Hook to check seller context status */
export function useSellerContextCheck() {
  return useQuery({
    queryKey: CONTEXT_QUERY_KEY,
    queryFn: checkSellerContext,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}

/** Hook to save quick setup context (JIT flow, snake_case API) */
export function useSaveQuickSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SellerQuickSetupFormState) => saveQuickSetupContext(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTEXT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    }
  });
}

/** Hook to get full seller profile */
export function useSellerProfile() {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: getSellerProfile,
    staleTime: 10 * 60 * 1000 // 10 minutes
  });
}

/** Hook to delete seller profile */
export function useDeleteSellerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSellerProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTEXT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    }
  });
}
